import { Link } from 'react-router-dom';
import { useOutbox } from '@dia/db';
import { useT } from '@dia/i18n';
import { RequireAuth, type Identity } from './lib/identity';
import { loadOpenRequests, loadStaysToday, useLoad } from './lib/data';
import { useLateSpanish } from './lib/requests';
import { requestText } from './lib/text';
import { formatDay } from './lib/time';
import { Notice, Page, Pill } from './lib/ui';

/**
 * Mensajes: one internal thread per request. This list is the open queue, and
 * it refreshes by itself every 30 seconds while the screen is open. Tapping a
 * request opens it, where its status is moved and the guest is answered.
 *
 * A guest request that reached the island in English only is translated here
 * the first time somebody looks with signal (migration 022), so the Spanish
 * lands for the whole crew rather than for this phone alone.
 */
export function Mensajes() {
  return <RequireAuth>{(who) => <ListBody who={who} />}</RequireAuth>;
}

function ListBody({ who }: { who: Identity }) {
  const { t, lang } = useT();
  const requests = useLoad(loadOpenRequests, 'requests', 30_000);
  const stays = useLoad(loadStaysToday, 'stays', 60_000);
  const { enqueue } = useOutbox();
  useLateSpanish(requests.data, who.token, enqueue);
  const list = requests.data ?? [];
  const guestStays = (stays.data ?? []).filter((s) => s.status !== 'cancelled').slice(-10).reverse();

  return (
    <Page title={t('ops.messages.title')} back="/mas">
      <p className="mb-3 text-inkMuted">{t('ops.messages.pick')}</p>
      {list.length === 0 && !requests.loading && <Notice tone="info">{t('ops.messages.empty_list')}</Notice>}
      <ul className="m-0 flex list-none flex-col gap-3 p-0">
        {list.map((r) => (
          <li key={r.id}>
            <Link
              to={`/mensajes/${r.id}`}
              className="flex min-h-tap flex-col gap-1 rounded border border-line bg-surface p-4 text-ink no-underline"
            >
              <span className="flex flex-wrap items-center gap-2">
                <Pill tone={r.status === 'new' ? 'warn' : 'neutral'}>{t(`ops.request.status.${r.status}`)}</Pill>
                <span className="font-semibold">{t(`ops.request.type.${r.type}`)}</span>
              </span>
              <span>{requestText(r, lang)}</span>
            </Link>
          </li>
        ))}
      </ul>

      {who.role !== 'crew' && guestStays.length > 0 && (
        <section className="mt-8">
          <h2 className="mb-1 text-[1.15em] font-semibold">{t('ops.messages.guests')}</h2>
          <p className="mb-3 text-inkMuted">{t('ops.messages.guests_hint')}</p>
          <ul className="m-0 flex list-none flex-col gap-3 p-0">
            {guestStays.map((s) => (
              <li key={s.id}>
                <Link
                  to={`/mensajes/huesped/${s.id}`}
                  className="flex min-h-tap flex-col gap-1 rounded border border-line bg-surface p-4 text-ink no-underline"
                >
                  <span className="font-semibold">
                    {t('ops.stays.dates_line', { from: formatDay(s.arrives_on, lang), to: formatDay(s.departs_on, lang) })}
                  </span>
                  <span>{t('ops.stays.people_line', { adults: s.adults, children: s.children })}</span>
                </Link>
              </li>
            ))}
          </ul>
        </section>
      )}
    </Page>
  );
}
